import { ReactElement, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Box, Divider } from '@breathelife/mui';
import { ParticipantMedicalInterviewWithParticipant } from '@breathelife/types';
import { Loader } from '@breathelife/ui-components';
import { downloadFileFromBlob } from '@breathelife/file-helpers';

import { Tabs } from '../../../../Components/Tabs/Tabs';
import { Button } from '../../../../Components/Button/Button';
import { Icon } from '../../../../Components/Icons/Icon';
import { MonospaceTypography } from '../../../../Components/Typography';
import { useFindApplicationSupportMedicalInterviewQuery } from '../../../../ReactQuery/ApplicationSupport/MedicalInterview/medicalInterview.queries';
import { MedicalInterviewView, XmlDisplayContainer, XmlHeaderContainer, XmlContentContainer } from './Styles';

const hasMedicalInterviews = (data: ParticipantMedicalInterviewWithParticipant[] | null | undefined): boolean => {
  return !!data?.length;
};

export function MedicalInterviewViewContainer(): ReactElement {
  const { t } = useTranslation();
  const { applicationId = '' } = useParams<{ applicationId: string }>();
  const [selectedParticipantTab, setSelectedParticipantTab] = useState(0);

  const { data: medicalInterviewData, isFetched } = useFindApplicationSupportMedicalInterviewQuery(applicationId);

  const participantTabs = medicalInterviewData?.length
    ? medicalInterviewData.map((mid: ParticipantMedicalInterviewWithParticipant) => ({
        label:
          'participant' in mid
            ? mid.participant?.data?.firstName + ' ' + mid.participant?.data?.lastName
            : 'Unknown Participant',
      }))
    : [];

  const onSwitchTabs = (selectedParticipant: number): void => {
    setSelectedParticipantTab(selectedParticipant);
  };

  const formatXml = (xml: string): string => {
    try {
      const formattedXml = xml
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t')
        .replace(/\\r/g, '\r')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\')
        .replace(/^"/, '')
        .replace(/"$/, '')
        .trim();

      return formattedXml;
    } catch {
      return xml;
    }
  };

  const onDownloadXml = useCallback(() => {
    if (!medicalInterviewData || !medicalInterviewData[selectedParticipantTab]) return;

    const currentInterview = medicalInterviewData[selectedParticipantTab];
    const xmlContent = 'requestXML' in currentInterview ? currentInterview.requestXML : undefined;

    if (!xmlContent) return;

    // Format XML for better readability
    const formattedXml = formatXml(xmlContent);
    const blob = new Blob([formattedXml], { type: 'application/xml' });

    const participantName =
      'participant' in currentInterview
        ? `${currentInterview.participant?.data?.firstName}_${currentInterview.participant?.data?.lastName}`.replace(
            /\s+/g,
            '_',
          )
        : 'Unknown_Participant';

    const fileName = `${applicationId}_${participantName}_medical_interview_request.xml`;
    downloadFileFromBlob(blob, fileName);
  }, [medicalInterviewData, selectedParticipantTab, applicationId]);

  if (!hasMedicalInterviews(medicalInterviewData)) {
    return <Box p='1.1rem'>{t('applicationSupport.medicalInterview.noMedicalInterviewsFound')}</Box>;
  }

  const currentInterview = medicalInterviewData?.[selectedParticipantTab];
  const xmlContent = currentInterview && 'requestXML' in currentInterview ? currentInterview.requestXML : undefined;

  return !isFetched ? (
    <Loader />
  ) : (
    <MedicalInterviewView>
      <Tabs
        value={selectedParticipantTab}
        onChange={(_, selectedParticipant) => onSwitchTabs(selectedParticipant)}
        tabs={participantTabs}
      />
      <Divider />

      {xmlContent && (
        <XmlDisplayContainer>
          <XmlHeaderContainer>
            <Button
              variant='outlined'
              color='primary'
              startIcon={<Icon name='download' />}
              onClick={onDownloadXml}
              size='small'
            >
              {t('cta.downloadFile.singular')}
            </Button>
          </XmlHeaderContainer>
          <XmlContentContainer>
            <MonospaceTypography variant='body2' component='pre'>
              {formatXml(xmlContent)}
            </MonospaceTypography>
          </XmlContentContainer>
        </XmlDisplayContainer>
      )}
    </MedicalInterviewView>
  );
}
